import { Star, Download, Trash, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { motion } from "framer-motion";
import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useCurrencyBalance } from "@/hooks/use-currency";
import { useAuth } from "@/hooks/use-auth";
import { useToast } from "@/hooks/use-toast";
import type { CardDeck } from "@shared/schema";

interface DeckCardProps {
  readonly deck: CardDeck;
  readonly viewMode?: 'grid' | 'list';
  readonly onSelect?: (deck: CardDeck) => void;
}

export function DeckCard({ deck, viewMode = 'grid', onSelect }: Readonly<DeckCardProps>) {
  const { token } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [purchaseDialogOpen, setPurchaseDialogOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Get user's currency balance
  const { data: balance } = useCurrencyBalance();

  const { data: userDecks } = useQuery({
    queryKey: ["user-decks"],
    queryFn: async () => {
      const response = await fetch("/api/deck/my-decks", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (!response.ok) throw new Error("Failed to fetch user decks");
      return response.json();
    },
  });

  const isSaved = userDecks?.some(
    (userDeck: any) => userDeck.cardDeckId === deck.id,
  );
  
  const isDefault = userDecks?.some(
    (userDeck: any) => userDeck.cardDeckId === deck.id && userDeck.default,
  );
  
  // Check if this is the system default deck
  const isSystemDefault = deck.id === -1;

  // Function to get membership badge styling
  const getMembershipBadge = () => {
    if (isSystemDefault) {
      return { bg: 'bg-blue-500/20', text: 'text-blue-500', label: 'System Default' };
    }
    switch (deck.membership) {
      case 'free':
        return { bg: 'bg-green-500/20', text: 'text-green-500', label: 'Free' };
      case 'premium':
        return { bg: 'bg-amber-500/20', text: 'text-amber-500', label: 'Premium' };
      case 'exclusive':
        return { bg: 'bg-purple-500/20', text: 'text-purple-500', label: 'Exclusive' };
      default:
        return { bg: 'bg-gray-500/20', text: 'text-gray-500', label: deck.membership };
    }
  };
  
  // Format price display
  const getPriceDisplay = () => {
    if (deck.price === null || deck.price === undefined || Number(deck.price) === 0) {
      return null;
    }
    
    // Convert to integer (remove decimal places)
    const priceValue = Math.floor(Number(deck.price));
    return { 
      value: priceValue,
      label: `${priceValue} Coins`
    };
  };
  
  const membershipBadge = getMembershipBadge();
  const priceDisplay = getPriceDisplay();
  const price = priceDisplay ? priceDisplay.value : 0;
  const currencyType = deck.currencyType || 'regular';
  
  // Check if user has sufficient funds for the purchase
  const hasSufficientFunds = balance && (
    currencyType === 'premium' 
      ? (balance.premiumCurrency >= price)
      : (balance.currency >= price)
  );

  // Handler to show purchase dialog or directly acquire free decks
  const handleDeckAction = async (e: React.MouseEvent) => {
    e.stopPropagation();
    
    // If it's a free deck or has price 0, acquire directly
    if (!priceDisplay || price === 0) {
      await acquireDeck();
      return;
    }
    
    // Otherwise, show purchase confirmation dialog
    setPurchaseDialogOpen(true);
  };
  
  // Handler for acquiring decks (both free and paid)
  const acquireDeck = async () => {
    if (isSystemDefault) return; // Prevent acquiring system default
    setIsSubmitting(true);
    try {
      const response = await fetch(
        `/api/deck/acquire/${deck.id}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        },
      );
      
      if (!response.ok) {
        const errorData = await response.json();
        
        if (response.status === 400 && errorData.message === "Insufficient funds") {
          toast({
            variant: "destructive",
            title: "Insufficient Funds",
            description: `You don't have enough ${currencyType === 'premium' ? 'gems' : 'coins'} to purchase this deck.`,
          });
          return;
        }
        
        throw new Error(errorData.message ?? "Failed to acquire deck");
      }
      
      const data = await response.json();
      
      await queryClient.invalidateQueries({ queryKey: ["user-decks"] });
      
      // If it was a purchase, also invalidate currency balance
      if (data.isPurchase) {
        await queryClient.invalidateQueries({ queryKey: ['/api/currency/balance'] });
        await queryClient.invalidateQueries({ queryKey: ['/api/currency/transactions'] });
        
        toast({
          title: "Purchase Successful",
          description: `You've purchased ${deck.name} for ${data.price} ${data.currencyType === 'premium' ? 'gems' : 'coins'}.`,
        });
      } else {
        toast({
          title: "Success",
          description: "Deck added to your collection!",
        });
      }
      
      setPurchaseDialogOpen(false);
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message ?? "Failed to acquire deck",
      });
    } finally {
      setIsSubmitting(false);
    }
  };
  
  // Handler for set as default action
  const handleSetDefault = async (e: React.MouseEvent) => {
    if (isSystemDefault) return; // Prevent setting system default
    e.stopPropagation();
    try {
      const response = await fetch(
        `/api/deck/set-default/${deck.id}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        },
      );
      if (!response.ok)
        throw new Error("Failed to set deck as default");

      await queryClient.invalidateQueries({ queryKey: ["default-deck"] });
      await queryClient.invalidateQueries({ queryKey: ["user-decks"] });

      toast({
        title: "Success",
        description: "Deck set as default!",
      });
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to set deck as default",
      });
    }
  };
  
  // Handler for remove deck action
  const handleRemoveDeck = async (e: React.MouseEvent) => {
    if (isSystemDefault) return; // Prevent removing system default
    e.stopPropagation();
    try {
      const response = await fetch(
        `/api/deck/remove/${deck.id}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
      if (!response.ok)
        throw new Error("Failed to remove deck");
      await queryClient.invalidateQueries({ queryKey: ["user-decks"] });
      toast({
        title: "Success",
        description: "Deck removed from your collection!",
      });
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to remove deck",
      });
    }
  };

  if (viewMode === 'list') {
    // List View Layout
    return (
      <div 
        className="bg-black/60 backdrop-blur-sm rounded-xl border border-primary/40 transition-all hover:border-primary hover:bg-black/70 overflow-hidden"
        onClick={() => onSelect?.(deck)}
      >
        <div className="flex items-center p-4">
          {/* Card Preview (left side) */}
          <div className="relative w-24 h-32 mr-4 flex-shrink-0">
            <div className="w-full h-full relative">
              {/* 6 of Hearts (back) */}
              <motion.div 
                className="absolute inset-0"
                initial={{ rotate: 0, translateX: 0 }}
                animate={{ 
                  rotate: '-6deg',
                  translateX: '-2px',
                  zIndex: 1,
                }}
                whileHover={{ y: -3, scale: 1.02 }}
                transition={{ type: "spring", stiffness: 300, damping: 20 }}
              >
                <img
                  src={`${deck.deckUrl}/B6.svg`}
                  alt="Six of Hearts"
                  className="w-full h-full object-contain rounded shadow-md"
                  onError={(e) => {
                    e.currentTarget.src = `${deck.deckUrl}/back.svg`;
                  }}
                />
              </motion.div>
              
              {/* King of Hearts (front) */}
              <motion.div 
                className="absolute inset-0"
                initial={{ rotate: 0, translateX: 0 }}
                animate={{ 
                  rotate: '6deg',
                  translateX: '8px',
                  zIndex: 2,
                }}
                whileHover={{ y: -3, scale: 1.02 }}
                transition={{ type: "spring", stiffness: 300, damping: 20 }}
              >
                <img
                  src={`${deck.deckUrl}/B13.svg`}
                  alt="King of Hearts"
                  className="w-full h-full object-contain rounded shadow-md"
                  onError={(e) => {
                    e.currentTarget.src = `${deck.deckUrl}/back.svg`;
                  }}
                />
              </motion.div>
              
            </div>
          </div>
          
          {/* Deck Info (middle) */}
          <div className="flex-grow">
            <div className="flex items-center gap-2 mb-1">
              <h3 className="font-bold text-lg">{deck.name}</h3>
              {isDefault && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Star className="h-4 w-4 text-yellow-400 fill-yellow-400" />
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>This is your default deck</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
            </div>
            
            <div className="flex flex-wrap gap-2 mt-2">
              
              <Badge variant="outline" className="bg-black/20">
                {deck.deckType}
              </Badge>
              {deck.isTradeable && (
                <Badge variant="outline" className="bg-blue-500/10 text-blue-400">
                  Tradeable
                </Badge>
              )}
              {priceDisplay && (
                <Badge variant="outline" className="bg-emerald-500/10 text-emerald-400">
                  {priceDisplay.label}
                </Badge>
              )}
            </div>
          </div>
          
          {/* Actions (right side) */}
          <div className="flex items-center gap-2">
            {!isSaved ? (
              <Button 
                variant="outline" 
                className="gap-2 bg-primary/10 border-primary/30 hover:bg-primary/20"
                onClick={handleDeckAction}
              >
                <Download className="h-4 w-4" />
                <span>Save</span>
              </Button>
            ) : (
              <div className="flex gap-2">
                {!isDefault && (
                  <Button 
                    variant="outline" 
                    className="gap-1 bg-yellow-500/10 border-yellow-500/30 hover:bg-yellow-500/20 text-yellow-500"
                    onClick={handleSetDefault}
                  >
                    <Star className="h-4 w-4" />
                    <span className="hidden sm:inline">Set Default</span>
                  </Button>
                )}
                <Button 
                  variant="outline" 
                  size="icon"
                  className="bg-red-500/10 border-red-500/30 hover:bg-red-500/20 text-red-500"
                  onClick={handleRemoveDeck}
                >
                  <Trash className="h-4 w-4" />
                </Button>
              </div>
            )}
            
            <ChevronRight className="h-5 w-5 text-muted-foreground" />
          </div>
        </div>
      </div>
    );
  }
  
  // Grid View Layout (default)
  return (
    <div 
      className="group bg-black/60 backdrop-blur-sm rounded-xl border border-primary/30 overflow-hidden transition-all hover:border-primary hover:bg-black/70"
      onClick={() => onSelect?.(deck)}
    >
      {/* Deck title and badges */}
      <div className="p-4 pt-3 pb-2">
        <h3 className="text-xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-white to-primary">{deck.name}</h3>
        
        <div className="flex flex-wrap gap-2 mt-2">
          <Badge className={cn("font-medium", membershipBadge.bg, membershipBadge.text)}>
            {membershipBadge.label}
          </Badge>
          <Badge variant="outline" className="bg-black/20 capitalize">
            {deck.deckType}
          </Badge>
          {isDefault && (
            <Badge className="bg-yellow-500/20 text-yellow-500 font-medium">
              <Star className="h-3 w-3 mr-1 fill-yellow-500" />
              Default
            </Badge>
          )}
          {priceDisplay && (
            <Badge className="bg-emerald-500/20 text-emerald-400 font-medium">
              {priceDisplay.label}
            </Badge>
          )}
        </div>
      </div>
      
      {/* Card Preview */}
      <div className="relative h-52 bg-gradient-to-br from-black to-black/20 px-4 overflow-hidden">
        <div className="h-full flex justify-center items-center">
          <div className="relative w-40 h-48">
            {/* 6 of Hearts (back) */}
            <motion.div
              className="absolute inset-0 w-full h-full rounded-lg shadow-xl transform -rotate-6 -translate-x-3"
              whileHover={{ y: -5, scale: 1.02 }}
              transition={{ type: "spring", stiffness: 300, damping: 15 }}
              style={{ zIndex: 1 }}
            >
              <img
                src={`${deck.deckUrl}/B6.svg`} /* 6 of Hearts */
                alt={`${deck.name} preview - Six`}
                className="w-full h-full object-contain rounded-md"
                onError={(e) => {
                  e.currentTarget.src = `${deck.deckUrl}/back.svg`;
                }}
              />
            </motion.div>
            
            {/* King of Hearts (front) */}
            <motion.div
              className="absolute inset-0 w-full h-full rounded-lg shadow-xl transform rotate-6 translate-x-3"
              whileHover={{ y: -5, scale: 1.05 }}
              transition={{ type: "spring", stiffness: 300, damping: 15 }}
              style={{ zIndex: 2 }}
            >
              <img
                src={`${deck.deckUrl}/B13.svg`} /* King of Hearts */
                alt={`${deck.name} preview - King`}
                className="w-full h-full object-contain rounded-md"
                onError={(e) => {
                  e.currentTarget.src = `${deck.deckUrl}/back.svg`;
                }}
              />
            </motion.div>
          </div>
        </div>
      </div>
      
      {/* Action Button */}
      <div className="p-4 pt-2">
        {!isSaved ? (
          <Button 
            onClick={handleDeckAction} 
            className="w-full bg-green-600 hover:bg-green-700 text-white font-medium shadow-md border-0"
          >
            <span className="text-lg mr-1">+</span> Add to Collection
          </Button>
        ) : (
          <Button 
            variant="default"
            className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium shadow-md border-0"
            onClick={() => onSelect?.(deck)}
          >
            View Deck
            <ChevronRight className="h-4 w-4 ml-2" />
          </Button>
        )}
      </div>

      {/* Purchase Confirmation Dialog */}
      <Dialog open={purchaseDialogOpen} onOpenChange={setPurchaseDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Purchase</DialogTitle>
            <DialogDescription>
              Are you sure you want to purchase <strong>{deck.name}</strong> for {price} {currencyType === 'premium' ? 'gems' : 'coins'}?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPurchaseDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={acquireDeck} 
              disabled={!hasSufficientFunds || isSubmitting}
            >
              {isSubmitting ? 'Processing...' : 'Confirm'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}